import { type Ref, ref } from "vue";

import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";

import { QUERY_KEYS } from "@/helpers/constants/queryKeys";
import { FIVE_MINUTES } from "@/helpers/constants/time";
import { formatPayloadWithCurrency } from "@/helpers/formatting";
import fundersApiService from "@/services/modules/funders";
import usersApiService from "@/services/modules/users";

import type {
  FunderContact,
  GetFundersPayload,
  IFunder,
} from "@/models/funders";

const FUNDERS_CHUNK_SIZE = 50;

export const FUNDERS_QUERY_KEYS = {
  SINGLE: "funder-single",
  LIST: "funder-list",
  USERS: "funder-users",
} as const;

export interface FunderUsersQueryParams extends Record<string, unknown> {
  funderId?: string;
  [key: string]: unknown;
}

export const usePlacementFundersQuery = (
  params: GetFundersPayload | (() => GetFundersPayload)
) => {
  return useQuery({
    queryKey: [...QUERY_KEYS.FUNDERS.all, params],
    enabled: () => {
      const resolvedParams = typeof params === "function" ? params() : params;
      return !!resolvedParams.ids?.length;
    },
    queryFn: async () => {
      const resolvedParams = typeof params === "function" ? params() : params;
      let allFundersResponse: IFunder[] = [];

      if (resolvedParams.ids?.length) {
        // Handle chunked requests for multiple IDs
        const chunk = (arr: string[], size: number): string[][] =>
          Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
            arr.slice(i * size, i * size + size)
          );

        const chunkedIds = chunk(resolvedParams.ids, FUNDERS_CHUNK_SIZE);
        const promises = chunkedIds.map((idChunk) =>
          fundersApiService.getFunders({ ...resolvedParams, ids: idChunk })
        );
        const responses = await Promise.all(promises);
        allFundersResponse = responses.flatMap((response) => response.data);
      } else {
        const response = await fundersApiService.getFunders(resolvedParams);
        allFundersResponse = response.data;
      }

      return allFundersResponse;
    },
  });
};

export const useFunderQuery = (id: Ref<IFunder["id"] | undefined>) => {
  return useQuery({
    queryKey: [FUNDERS_QUERY_KEYS.SINGLE, id],
    queryFn: async () => fundersApiService.getFunder(id.value!),
    enabled: () => !!id.value?.length,
    staleTime: FIVE_MINUTES,
  });
};

export const useFundersQuery = (
  params: Ref<GetFundersPayload>,
  enabled: Ref<boolean> = ref(true)
) => {
  return useQuery({
    queryKey: [FUNDERS_QUERY_KEYS.LIST, params],
    queryFn: async () => await fundersApiService.getFunders(params.value),
    enabled,
    staleTime: FIVE_MINUTES,
  });
};

export const useFetchApprovedFundersPage = () => {
  const queryClient = useQueryClient();

  return (params: GetFundersPayload = {}) => {
    const queryParams: GetFundersPayload = { ...params, approved: 1 };

    return queryClient.fetchQuery({
      queryKey: [FUNDERS_QUERY_KEYS.LIST, queryParams],
      queryFn: () => fundersApiService.getFunders(queryParams),
    });
  };
};

export const useFunderUsersQuery = (
  params: Ref<FunderUsersQueryParams | undefined>
) => {
  return useQuery({
    queryKey: [FUNDERS_QUERY_KEYS.USERS, params],
    queryFn: async () => {
      const { funderId, ...queryParams } = params.value!;
      return await fundersApiService.getFunderUsers(funderId!, queryParams);
    },
    enabled: () => !!params.value?.funderId,
    staleTime: FIVE_MINUTES,
  });
};

export const useCreateFunderMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: Partial<IFunder>) =>
      await fundersApiService.createFunder(
        formatPayloadWithCurrency(payload as Record<string, string | number>)
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FUNDERS_QUERY_KEYS.LIST],
      });
    },
  });
};

export const useCreateFunderUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      payload: Partial<FunderContact> & { funder_id: IFunder["id"] }
    ) => await fundersApiService.createFunderUser(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FUNDERS_QUERY_KEYS.USERS] });
    },
  });
};

export const useEditFunderUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      userId: string;
      data: Partial<FunderContact>;
    }) => await fundersApiService.editFunderUser(payload.userId, payload.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FUNDERS_QUERY_KEYS.USERS] });
    },
  });
};

export const useDeleteFunderUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: number) =>
      await usersApiService.deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FUNDERS_QUERY_KEYS.USERS] });
    },
  });
};

export const useAttachFunderLogoMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { funderId: IFunder["id"]; logo: File }) =>
      await fundersApiService.uploadFunderLogo(payload.funderId, payload.logo),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({
        queryKey: [FUNDERS_QUERY_KEYS.SINGLE, vars.funderId],
      });
    },
  });
};

export const useDeleteFunderLogoMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (funderId: IFunder["id"]) =>
      await fundersApiService.deleteFunderLogo(funderId),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: [FUNDERS_QUERY_KEYS.SINGLE, id],
      });
    },
  });
};

export const useDeleteFunderMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (funderId: IFunder["id"]) =>
      await fundersApiService.deleteFunder(funderId),
    onSuccess: (_, funderId) => {
      queryClient.invalidateQueries({
        queryKey: [FUNDERS_QUERY_KEYS.LIST],
      });
      queryClient.removeQueries({
        queryKey: [FUNDERS_QUERY_KEYS.USERS, { funderId }],
        type: "all",
      });
    },
  });
};

export const useApproveFunderMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (funderId: IFunder["id"]) =>
      await fundersApiService.approveFunder(funderId),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: [FUNDERS_QUERY_KEYS.LIST],
      });
      queryClient.invalidateQueries({
        queryKey: [FUNDERS_QUERY_KEYS.SINGLE, id],
      });
    },
  });
};

export const useUpdateFunderMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      funderId: IFunder["id"];
      data: Partial<IFunder>;
    }) => {
      return await fundersApiService.updateFunder(
        formatPayloadWithCurrency(
          payload.data as unknown as Record<string, string | number>
        ) as unknown as IFunder,
        payload.funderId
      );
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({
        queryKey: [FUNDERS_QUERY_KEYS.SINGLE, vars.funderId],
      });
      queryClient.invalidateQueries({
        queryKey: [FUNDERS_QUERY_KEYS.LIST],
      });
    },
  });
};
